import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate } from './organization/finance/shared';
import Icon from '../components/dashboard/DashboardIcons';
import { UsersTab, PartnersTab, TestimonialsTab, NotificationsTab, LandingTab, PlansTab, CoachesTab, PlatformUpdatesTab, AccountingTab, SupportInboxTab, QuotesTab } from './SuperAdminTabs';
import { DaysBar, daysRemaining, AdminWhatsAppModal } from './superadmin/subscriptionWidgets';
import { ActivityCell } from './superadmin/presence';
import CommandCenter from './superadmin/CommandCenter';
import ClubProfileDrawer from './superadmin/ClubProfileDrawer';
import { normalizeWhatsAppNumber, buildWaMeLink } from '../lib/whatsapp';

const sauth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const STATUS_LABELS = { active: 'نشطة', inactive: 'معطلة', pending: 'معلقة', suspended: 'موقوفة' };
const STATUS_CLS = { active: 'bg-teal-50 text-teal-700', inactive: 'bg-red-50 text-red-600', pending: 'bg-amber-50 text-amber-700', suspended: 'bg-slate-100 text-slate-500' };
const PLAN_LABELS = { free: 'مجانية', basic: 'أساسية', premium: 'مميزة', enterprise: 'مؤسسات' };

const StatCard = ({ label, value, accent, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-bold ${accent || 'text-slate-900'}`}>{value ?? '—'}</div>
  </div>
);

export default function SuperAdminPage() {
  const [tab, setTab] = useState('overview');
  const [toast, setToast] = useState(null);
  const [platform, setPlatform] = useState({ platform_name: 'تسيار', platform_logo: null });
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    axios.get(`${API}/platform/settings`).then((r) => setPlatform(r.data || {})).catch(() => {});
  }, []);

  let user = null;
  try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch { user = null; }
  const isSuper = user && (user.is_super_admin || user.role === 'super_admin');

  if (!isSuper) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" dir="rtl" data-testid="superadmin-denied">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <Icon name="warning" className="mx-auto h-10 w-10 text-amber-500" />
          <h2 className="mt-3 text-lg font-bold text-slate-900">هذه اللوحة مخصصة لمدير المنصة</h2>
          <p className="mt-1 text-sm text-slate-500">سجّل الدخول بحساب السوبر أدمن للمتابعة</p>
          <button onClick={() => window.location.replace('/login')} className="mt-4 rounded-lg bg-[#16233b] px-6 py-2 text-sm font-semibold text-white" data-testid="superadmin-goto-login">الذهاب لتسجيل الدخول</button>
        </div>
      </div>
    );
  }

  const tabs = [
    ['overview', 'نظرة عامة', 'home'],
    ['clubs', 'المنشآت والاشتراكات', 'building'],
    ['users', 'المستخدمون', 'users'],
    ['accounting', 'المحاسبة المالية', 'wallet'],
    ['plans', 'باقات الأسعار', 'banknotes'],
    ['quotes', 'عروض الأسعار', 'document'],
    ['coaches', 'منظومة المدربين', 'whistle'],
    ['requests', 'طلبات التسجيل', 'inbox'],
    ['venue-owners', 'أصحاب الملاعب', 'field'],
    ['support', 'صندوق الدعم', 'inbox'],
    ['partners', 'الشركاء', 'star'],
    ['testimonials', 'آراء العملاء', 'star'],
    ['notifications', 'الإشعارات والرسائل', 'bell'],
    ['updates', 'تحديثات المنصة', 'star'],
    ['landing', 'الواجهة الرئيسية', 'flag'],
    ['platform', 'إعدادات المنصة', 'cog'],
  ];

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl" data-testid="superadmin-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <header className="sticky top-0 z-40 bg-[#16233b] text-white shadow-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3" data-testid="superadmin-header-brand">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-white/10">
              {platform.platform_logo ? <img src={`${API}/files/${platform.platform_logo}`} alt={platform.platform_name || 'تسيار'} className="h-8 w-8 object-contain" /> : <Icon name="grid" className="w-5 h-5" />}
            </div>
            <div>
              <div className="text-sm font-bold" data-testid="superadmin-header-platform-name">{platform.platform_name || 'تسيار'}</div>
              <div className="text-[11px] text-slate-300">السوبر أدمن — إدارة المنشآت والاشتراكات</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-slate-300 sm:block">{user.full_name}</span>
            <button onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); delete axios.defaults.headers.common['Authorization']; window.location.replace('/login'); }} className="rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold hover:bg-white/10" data-testid="superadmin-logout">تسجيل الخروج</button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 pb-2" data-testid="superadmin-tabs">
          {tabs.map(([k, v, ic]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${tab === k ? 'bg-white text-[#16233b]' : 'text-slate-300 hover:bg-white/10'}`} data-testid={`superadmin-tab-${k}`}>
              <Icon name={ic} className="w-4 h-4" /> {v}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-4 px-4 py-6">
        {tab === 'overview' && <CommandCenter notify={notify} goTab={setTab} />}
        {tab === 'clubs' && <ClubsTab notify={notify} />}
        {tab === 'users' && <UsersTab notify={notify} />}
        {tab === 'accounting' && <AccountingTab notify={notify} />}
        {tab === 'plans' && <PlansTab notify={notify} />}
        {tab === 'quotes' && <QuotesTab notify={notify} />}
        {tab === 'coaches' && <CoachesTab notify={notify} />}
        {tab === 'requests' && <RequestsTab notify={notify} />}
        {tab === 'venue-owners' && <VenueOwnersTab notify={notify} />}
        {tab === 'support' && <SupportInboxTab notify={notify} />}
        {tab === 'partners' && <PartnersTab notify={notify} />}
        {tab === 'testimonials' && <TestimonialsTab notify={notify} />}
        {tab === 'notifications' && <NotificationsTab notify={notify} />}
        {tab === 'updates' && <PlatformUpdatesTab notify={notify} />}
        {tab === 'landing' && <LandingTab notify={notify} />}
        {tab === 'platform' && <PlatformTab notify={notify} />}
      </main>
    </div>
  );
}

function ClubsTab({ notify }) {
  const [clubs, setClubs] = useState([]);
  const [activity, setActivity] = useState({});
  const [profileClubId, setProfileClubId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {club, type: 'deactivate'|'activate'}
  const [subModal, setSubModal] = useState(null); // club being renewed/edited
  const [subForm, setSubForm] = useState({});
  const [subErr, setSubErr] = useState(null);
  const [waTarget, setWaTarget] = useState(null); // club for whatsapp modal
  const [confirmEnd, setConfirmEnd] = useState(null); // club to end subscription for

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/saas/admin/clubs?limit=100`, sauth()); setClubs(Array.isArray(res.data) ? res.data : []); } catch { setClubs([]); }
    try { const act = await axios.get(`${API}/saas/admin/clubs-activity`, sauth()); setActivity(act.data && typeof act.data === 'object' ? act.data : {}); } catch { setActivity({}); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (c) => {
    setEditing(c || null);
    setForm(c ? { ...c } : { name: '', slug: '', email: '', phone: '', city: '', description: '', admin_username: '', admin_full_name: '', admin_email: '', admin_password: '' });
    setErr(null); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!form.name?.trim()) { setErr('اسم المنشأة مطلوب'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/saas/admin/clubs/${editing.id}`, {
          name: form.name.trim(), email: form.email || null, phone: form.phone || null,
          city: form.city || null, description: form.description || null,
          contact_person: form.contact_person || null,
        }, sauth());
      } else {
        if (!form.slug?.trim() || !form.email?.trim()) { setErr('المعرف (slug) والبريد الإلكتروني مطلوبان'); return; }
        if (!form.admin_username?.trim() || !form.admin_full_name?.trim() || !form.admin_password) { setErr('بيانات مدير المنشأة (اسم الدخول، الاسم، كلمة المرور) مطلوبة'); return; }
        if (form.admin_password.length < 6) { setErr('كلمة مرور المدير يجب ألا تقل عن 6 أحرف'); return; }
        await axios.post(`${API}/saas/admin/clubs`, {
          name: form.name.trim(), slug: form.slug.trim().toLowerCase(), email: form.email.trim(),
          phone: form.phone || null, city: form.city || null, description: form.description || null,
          admin_username: form.admin_username.trim(), admin_full_name: form.admin_full_name.trim(),
          admin_email: form.admin_email || null, admin_password: form.admin_password,
        }, sauth());
      }
      setModal(false); notify('تم الحفظ بنجاح'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const doAction = async () => {
    const { club, type } = confirmAction;
    try {
      if (type === 'activate') await axios.post(`${API}/saas/admin/clubs/${club.id}/activate`, {}, sauth());
      else await axios.delete(`${API}/saas/admin/clubs/${club.id}`, sauth());
      notify(type === 'activate' ? 'تم تفعيل المنشأة ومستخدميها' : 'تم تعطيل المنشأة ومستخدميها');
      setConfirmAction(null); load();
    } catch (e2) { notify(errMsg(e2, 'فشلت العملية')); setConfirmAction(null); }
  };

  const openSubModal = (c) => {
    setSubModal(c);
    setSubForm({ plan: c.subscription_plan || 'free', status: c.subscription_status || 'active', end_date: c.subscription_end_date ? String(c.subscription_end_date).slice(0, 10) : '', amount: c.subscription_amount ?? '' });
    setSubErr(null);
  };

  const submitSub = async (e) => {
    e.preventDefault(); setSubErr(null);
    try {
      await axios.put(`${API}/saas/admin/clubs/${subModal.id}/subscription`, {
        plan: subForm.plan, status: subForm.status,
        end_date: subForm.end_date || null, amount: subForm.amount === '' ? null : Number(subForm.amount),
      }, sauth());
      setSubModal(null); notify('تم تحديث الاشتراك بنجاح'); load();
    } catch (e2) { setSubErr(errMsg(e2, 'فشل تحديث الاشتراك')); }
  };

  const endSubscription = async () => {
    try {
      await axios.put(`${API}/saas/admin/clubs/${confirmEnd.id}/subscription`, { status: 'cancelled' }, sauth());
      notify('تم إنهاء الاشتراك'); setConfirmEnd(null); load();
    } catch (e2) { notify(errMsg(e2, 'فشلت العملية')); setConfirmEnd(null); }
  };

  const filtered = clubs.filter((c) => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (planFilter && c.subscription_plan !== planFilter) return false;
    if (expiringOnly) {
      const dl = daysRemaining(c.subscription_end_date);
      if (dl === null || dl > 14) return false;
    }
    const q = search.trim();
    return !q || c.name?.includes(q) || c.slug?.includes(q) || c.city?.includes(q);
  });

  return (
    <div className="space-y-4" data-testid="superadmin-clubs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-slate-900">المنشآت ({clubs.length})</h2>
        <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700" data-testid="clubs-online-now-badge">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {Object.values(activity).filter((a) => a?.is_online).length} منشأة متصلة الآن
        </span>
        <PrimaryBtn onClick={() => openForm(null)} data-testid="club-add-btn"><Icon name="plus" className="w-4 h-4" /> إنشاء منشأة</PrimaryBtn>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالاسم أو المدينة..." className="w-48 bg-transparent text-sm outline-none" data-testid="club-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="club-status-filter">
            <option value="">كل الحالات</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="club-plan-filter">
            <option value="">كل الخطط</option>
            {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)} data-testid="club-expiring-filter" />
            قرب الانتهاء (≤14 يوم)
          </label>
          <span className="mr-auto text-xs text-slate-400">{filtered.length} منشأة</span>
        </div>
        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 text-right text-xs text-slate-500">
                  <th className="px-4 py-2 font-semibold">المنشأة</th>
                  <th className="px-2 py-2 font-semibold">التواصل</th>
                  <th className="px-2 py-2 font-semibold">الخطة</th>
                  <th className="px-2 py-2 font-semibold">الأيام المتبقية</th>
                  <th className="px-2 py-2 font-semibold">آخر دخول / الاتصال</th>
                  <th className="px-2 py-2 font-semibold">الحالة</th>
                  <th className="px-2 py-2"></th>
                </tr></thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid="club-row">
                      <td className="px-4 py-2">
                        <button type="button" onClick={() => setProfileClubId(c.id)} className="block text-right font-semibold text-slate-800 transition-colors hover:text-teal-700" data-testid="club-open-profile-btn">{c.name}</button>
                        <div className="text-xs text-slate-400" dir="ltr">/{c.slug}</div>
                      </td>
                      <td className="px-2 py-2">
                        {c.phone ? (
                          <a href={buildWaMeLink(normalizeWhatsAppNumber(c.phone), '')} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-teal-600" dir="ltr" data-testid="club-phone-link">
                            <Icon name="whatsapp" className="w-3.5 h-3.5" /> {c.phone}
                          </a>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-2 py-2"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{PLAN_LABELS[c.subscription_plan] || c.subscription_plan || '—'}</span></td>
                      <td className="px-2 py-2"><DaysBar endDate={c.subscription_end_date} testId="club-days-bar" /></td>
                      <td className="px-2 py-2">
                        <ActivityCell
                          testId="club-activity-cell"
                          online={activity[c.id]?.is_online}
                          lastLogin={activity[c.id]?.last_login}
                          lastSeen={activity[c.id]?.last_seen}
                          createdAt={c.created_at}
                          subject="لم يدخل أي مستخدم بعد"
                        />
                      </td>
                      <td className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_CLS[c.status] || ''}`} data-testid="club-status-badge">{STATUS_LABELS[c.status] || c.status}</span></td>
                      <td className="px-2 py-2">
                        <div className="flex justify-end gap-1">
                          <a href={`/club/${c.slug}`} target="_blank" rel="noreferrer" title="الصفحة العامة" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#16233b]" data-testid="club-public-link"><Icon name="flag" className="w-4 h-4" /></a>
                          <button onClick={() => setWaTarget(c)} title="تذكير دفع واتساب" className="rounded-lg p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600" data-testid="club-whatsapp-btn"><Icon name="whatsapp" className="w-4 h-4" /></button>
                          <button onClick={() => openSubModal(c)} title="تجديد / تعديل الاشتراك" className="rounded-lg p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600" data-testid="club-renew-btn"><Icon name="refresh" className="w-4 h-4" /></button>
                          <button onClick={() => openForm(c)} title="تعديل" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="club-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                          {c.status === 'active'
                            ? <button onClick={() => setConfirmAction({ club: c, type: 'deactivate' })} title="تعطيل" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="club-deactivate-btn"><Icon name="x" className="w-4 h-4" /></button>
                            : <button onClick={() => setConfirmAction({ club: c, type: 'activate' })} title="تفعيل" className="rounded-lg p-1.5 text-slate-500 hover:bg-teal-50 hover:text-teal-700" data-testid="club-activate-btn"><Icon name="check" className="w-4 h-4" /></button>}
                          <button onClick={() => setConfirmEnd(c)} title="إنهاء الاشتراك" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="club-end-sub-btn"><Icon name="lock" className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <Modal open={modal} title={editing ? `تعديل: ${editing.name}` : 'إنشاء منشأة جديدة'} onClose={() => setModal(false)} wide>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="club-form-error">{err}</div>}
        <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="اسم المنشأة" required><input className={inputCls} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="club-input-name" /></Field>
          {!editing && <Field label="المعرف بالإنجليزية (slug)" required><input dir="ltr" placeholder="my-club" className={inputCls} value={form.slug || ''} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="club-input-slug" /></Field>}
          <Field label="البريد الإلكتروني" required={!editing}><input dir="ltr" type="email" className={inputCls} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="club-input-email" /></Field>
          <Field label="الهاتف"><input dir="ltr" className={inputCls} value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="المدينة"><input className={inputCls} value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <div className="col-span-full"><Field label="الوصف"><textarea rows={2} className={inputCls} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field></div>
          {!editing && (
            <>
              <div className="col-span-full mt-1 border-t border-slate-100 pt-2 text-sm font-bold text-slate-700">حساب مدير المنشأة</div>
              <Field label="اسم الدخول" required><input dir="ltr" className={inputCls} value={form.admin_username || ''} onChange={(e) => setForm({ ...form, admin_username: e.target.value })} data-testid="club-input-admin-username" /></Field>
              <Field label="الاسم الكامل" required><input className={inputCls} value={form.admin_full_name || ''} onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} data-testid="club-input-admin-fullname" /></Field>
              <Field label="بريد المدير"><input dir="ltr" type="email" className={inputCls} value={form.admin_email || ''} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} /></Field>
              <Field label="كلمة المرور" required><input dir="ltr" type="password" className={inputCls} value={form.admin_password || ''} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="club-input-admin-password" /></Field>
            </>
          )}
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="club-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmAction)} title={confirmAction?.type === 'activate' ? 'تفعيل المنشأة' : 'تعطيل المنشأة'} onClose={() => setConfirmAction(null)}>
        <p className="text-sm text-slate-600">
          {confirmAction?.type === 'activate'
            ? `سيتم تفعيل «${confirmAction?.club?.name}» وإعادة تفعيل جميع مستخدميها. متابعة؟`
            : `سيتم تعطيل «${confirmAction?.club?.name}» وإيقاف دخول جميع مستخدميها (لا تُحذف البيانات). متابعة؟`}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmAction(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={doAction} className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${confirmAction?.type === 'activate' ? 'bg-teal-600' : 'bg-red-600'}`} data-testid="club-confirm-action">{confirmAction?.type === 'activate' ? 'تفعيل' : 'تعطيل'}</button>
        </div>
      </Modal>

      <Modal open={Boolean(subModal)} title={`تجديد / تعديل اشتراك: ${subModal?.name || ''}`} onClose={() => setSubModal(null)}>
        {subErr && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="club-sub-error">{subErr}</div>}
        <form onSubmit={submitSub} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="الخطة">
            <select className={inputCls} value={subForm.plan} onChange={(e) => setSubForm({ ...subForm, plan: e.target.value })} data-testid="club-sub-plan">
              {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Field>
          <Field label="الحالة">
            <select className={inputCls} value={subForm.status} onChange={(e) => setSubForm({ ...subForm, status: e.target.value })} data-testid="club-sub-status">
              <option value="active">نشط</option>
              <option value="trial">تجريبي</option>
              <option value="expired">منتهي</option>
              <option value="cancelled">ملغي</option>
              <option value="pending">معلق</option>
            </select>
          </Field>
          <Field label="تاريخ نهاية الاشتراك"><input type="date" className={inputCls} value={subForm.end_date} onChange={(e) => setSubForm({ ...subForm, end_date: e.target.value })} data-testid="club-sub-end-date" /></Field>
          <Field label="المبلغ المستحق (ريال)"><input type="number" min="0" step="0.01" className={inputCls} value={subForm.amount} onChange={(e) => setSubForm({ ...subForm, amount: e.target.value })} data-testid="club-sub-amount" /></Field>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setSubModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="club-sub-submit">حفظ الاشتراك</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmEnd)} title="إنهاء الاشتراك" onClose={() => setConfirmEnd(null)}>
        <p className="text-sm text-slate-600">سيتم تحويل حالة اشتراك «{confirmEnd?.name}» إلى ملغي فوراً. هذا لا يعطّل حساب المنشأة نفسه (استخدم زر التعطيل لذلك). متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmEnd(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={endSubscription} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="club-confirm-end-sub">إنهاء الاشتراك</button>
        </div>
      </Modal>

      <AdminWhatsAppModal
        open={Boolean(waTarget)}
        onClose={() => setWaTarget(null)}
        type="club_subscription"
        entity={waTarget ? { name: waTarget.name, phone: waTarget.phone, plan_name: PLAN_LABELS[waTarget.subscription_plan] || waTarget.subscription_plan, expiry_date: waTarget.subscription_end_date, amount: waTarget.subscription_amount } : null}
      />

      <ClubProfileDrawer
        clubId={profileClubId}
        onClose={() => setProfileClubId(null)}
        onChanged={load}
        notify={notify}
        onWhatsApp={(e) => { setProfileClubId(null); setWaTarget({ name: e.name, phone: e.phone, subscription_plan: null, subscription_end_date: e.expiry_date, subscription_amount: e.amount }); }}
      />
    </div>
  );
}

function VenueOwnersTab({ notify }) {
  const [listings, setListings] = useState([]);
  const [upgrades, setUpgrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, u] = await Promise.all([
        axios.get(`${API}/venues/admin/pending-listings`, sauth()).then((r) => r.data).catch(() => []),
        axios.get(`${API}/saas/admin/venue-owner-upgrade-requests`, { ...sauth(), params: { status: 'pending' } }).then((r) => r.data).catch(() => []),
      ]);
      setListings(l || []); setUpgrades(u || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approveListing = async (v) => {
    try { await axios.post(`${API}/venues/admin/${v.id}/approve-listing`, {}, sauth()); notify('تم اعتماد نشر الملعب'); load(); }
    catch (e) { notify(errMsg(e, 'فشلت العملية')); }
  };
  const rejectListing = async () => {
    try { await axios.post(`${API}/venues/admin/${rejectTarget.id}/reject-listing`, { reason: rejectReason }, sauth()); notify('تم رفض نشر الملعب'); setRejectTarget(null); setRejectReason(''); load(); }
    catch (e) { notify(errMsg(e, 'فشلت العملية')); }
  };
  const approveUpgrade = async (r) => {
    try { await axios.post(`${API}/saas/admin/venue-owner-upgrade-requests/${r.id}/approve`, {}, sauth()); notify('تمت ترقية الحساب لمنشأة كاملة'); load(); }
    catch (e) { notify(errMsg(e, 'فشلت العملية')); }
  };
  const rejectUpgrade = async (r) => {
    try { await axios.post(`${API}/saas/admin/venue-owner-upgrade-requests/${r.id}/reject`, { reason: 'غير مكتمل' }, sauth()); notify('تم رفض طلب الترقية'); load(); }
    catch (e) { notify(errMsg(e, 'فشلت العملية')); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-venue-owners">
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">ملاعب بانتظار اعتماد النشر ({listings.length})</div>
        {loading ? <div className="p-4"><div className="h-14 animate-pulse rounded-lg bg-slate-100" /></div>
          : listings.length === 0 ? <p className="p-6 text-center text-sm text-slate-400" data-testid="venue-listings-empty">لا توجد ملاعب بانتظار الاعتماد</p>
          : (
            <div className="divide-y divide-slate-50">
              {listings.map((v) => (
                <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid={`venue-listing-row-${v.id}`}>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{v.name} — {v.club_name}</div>
                    <div className="text-xs text-slate-500">{v.city || '—'} · {v.club_phone || v.club_email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => approveListing(v)} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid={`venue-listing-approve-${v.id}`}>اعتماد النشر</button>
                    <button onClick={() => setRejectTarget(v)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid={`venue-listing-reject-${v.id}`}>رفض</button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">طلبات الترقية إلى منشأة كاملة ({upgrades.length})</div>
        {loading ? <div className="p-4"><div className="h-14 animate-pulse rounded-lg bg-slate-100" /></div>
          : upgrades.length === 0 ? <p className="p-6 text-center text-sm text-slate-400" data-testid="upgrade-requests-empty">لا توجد طلبات ترقية</p>
          : (
            <div className="divide-y divide-slate-50">
              {upgrades.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid={`upgrade-request-row-${r.id}`}>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{r.club_name}</div>
                    <div className="text-xs text-slate-500">بواسطة {r.requested_by_name} · {fmtDate(r.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => approveUpgrade(r)} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid={`upgrade-request-approve-${r.id}`}>موافقة</button>
                    <button onClick={() => rejectUpgrade(r)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid={`upgrade-request-reject-${r.id}`}>رفض</button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <Modal open={Boolean(rejectTarget)} title="رفض نشر الملعب" onClose={() => setRejectTarget(null)}>
        <Field label="سبب الرفض (اختياري)"><input className={inputCls} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="venue-listing-reject-reason-input" /></Field>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setRejectTarget(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">إلغاء</button>
          <PrimaryBtn onClick={rejectListing} data-testid="venue-listing-reject-confirm-btn">تأكيد الرفض</PrimaryBtn>
        </div>
      </Modal>
    </div>
  );
}

function RequestsTab({ notify }) {
  const [data, setData] = useState({ requests: [], pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/saas/admin/registration-requests`, sauth()); setData(res.data || { requests: [] }); } catch { setData({ requests: [] }); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (req, status) => {
    try { await axios.put(`${API}/saas/admin/registration-requests/${req.id}`, { status }, sauth()); notify(status === 'approved' ? 'تمت الموافقة على الطلب' : 'تم رفض الطلب'); load(); }
    catch (e2) { notify(errMsg(e2, 'فشلت العملية')); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/saas/admin/registration-requests/${confirmDel.id}`, sauth()); notify('تم حذف الطلب'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const REQ_STATUS = { pending: ['قيد المراجعة', 'bg-amber-50 text-amber-700'], approved: ['مقبول', 'bg-teal-50 text-teal-700'], rejected: ['مرفوض', 'bg-red-50 text-red-600'] };

  return (
    <div className="space-y-4" data-testid="superadmin-requests">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="قيد المراجعة" value={data.pending} accent="text-amber-600" testId="req-pending" />
        <StatCard label="مقبولة" value={data.approved} accent="text-teal-700" />
        <StatCard label="مرفوضة" value={data.rejected} accent="text-red-600" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">طلبات تسجيل المنشآت ({data.total || 0})</div>
        {loading ? <div className="space-y-2 p-4">{[1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : (data.requests || []).length === 0 ? <p className="p-6 text-center text-sm text-slate-400" data-testid="requests-empty">لا توجد طلبات تسجيل</p>
          : (
            <div className="divide-y divide-slate-50">
              {data.requests.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid="request-row">
                  <div>
                    <div className="text-sm font-bold text-slate-900">{r.club_name}</div>
                    <div className="text-xs text-slate-500">{r.contact_person} · {r.email} {r.phone ? `· ${r.phone}` : ''} {r.city ? `· ${r.city}` : ''}</div>
                    {r.message && <div className="mt-0.5 text-xs text-slate-400">{r.message}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${REQ_STATUS[r.status]?.[1] || ''}`}>{REQ_STATUS[r.status]?.[0] || r.status}</span>
                    {r.status === 'pending' && (
                      <>
                        <button onClick={() => setStatus(r, 'approved')} className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid="request-approve-btn">موافقة</button>
                        <button onClick={() => setStatus(r, 'rejected')} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white" data-testid="request-reject-btn">رفض</button>
                      </>
                    )}
                    <button onClick={() => setConfirmDel(r)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="request-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
      <Modal open={Boolean(confirmDel)} title="حذف الطلب" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف طلب «{confirmDel?.club_name}» نهائياً؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="request-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}

function PlatformTab({ notify }) {
  const [form, setForm] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingDark, setUploadingDark] = useState(false);
  const [uploadingLight, setUploadingLight] = useState(false);
  const [uploadingBadge, setUploadingBadge] = useState(false);

  useEffect(() => {
    axios.get(`${API}/platform/settings`).then((r) => setForm(r.data)).catch(() => setForm({}));
  }, []);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setErr(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      const fileId = res.data?.file_id;
      await axios.post(`${API}/platform/settings`, { ...form, platform_logo: fileId, platform_favicon: fileId }, sauth());
      setForm((f) => ({ ...f, platform_logo: fileId, platform_favicon: fileId }));
      notify('تم تحديث شعار المنصة');
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الشعار')); }
    setUploading(false);
  };

  const uploadThemeLogo = async (e, mode) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const setUploadingFn = mode === 'dark' ? setUploadingDark : setUploadingLight;
    const field = mode === 'dark' ? 'platform_logo_full_dark' : 'platform_logo_full_light';
    setUploadingFn(true); setErr(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      const fileId = res.data?.file_id;
      await axios.post(`${API}/platform/settings`, { ...form, [field]: fileId }, sauth());
      setForm((f) => ({ ...f, [field]: fileId }));
      notify(mode === 'dark' ? 'تم تحديث الشعار الليلي' : 'تم تحديث الشعار النهاري');
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الشعار')); }
    setUploadingFn(false);
  };

  const uploadSaudiBadgeLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingBadge(true); setErr(null);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, sauth());
      const fileId = res.data?.file_id;
      await axios.post(`${API}/platform/settings`, { ...form, saudi_badge_logo_file_id: fileId }, sauth());
      setForm((f) => ({ ...f, saudi_badge_logo_file_id: fileId }));
      notify('تم تحديث شعار رؤية 2030');
    } catch (e2) { setErr(errMsg(e2, 'فشل رفع الشعار')); }
    setUploadingBadge(false);
  };

  const toggleSaudiBadge = async (checked) => {
    setForm((f) => ({ ...f, saudi_badge_enabled: checked }));
    try {
      await axios.post(`${API}/platform/settings`, { ...form, saudi_badge_enabled: checked }, sauth());
      notify(checked ? 'تم تفعيل شارة رؤية 2030' : 'تم إخفاء شارة رؤية 2030');
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const save = async (e) => {
    e.preventDefault(); setErr(null); setSaving(true);
    try {
      await axios.post(`${API}/platform/settings`, {
        ...form,
        platform_name: form.platform_name || '',
        contact_email: form.contact_email || '',
        contact_phone: form.contact_phone || '',
        twitter_url: form.twitter_url || null,
        instagram_url: form.instagram_url || null,
        youtube_url: form.youtube_url || null,
        snapchat_url: form.snapchat_url || null,
        tiktok_url: form.tiktok_url || null,
        primary_color: form.primary_color || '#3b82f6',
        secondary_color: form.secondary_color || '#10b981',
      }, sauth());
      notify('تم حفظ إعدادات المنصة');
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
    setSaving(false);
  };

  if (!form) return <div className="h-48 animate-pulse rounded-xl bg-slate-100" />;
  const logoUrl = form.platform_logo ? `${API}/files/${form.platform_logo}` : null;
  const darkLogoId = form.platform_logo_full_dark || form.platform_logo_full_light || form.platform_logo_full;
  const lightLogoId = form.platform_logo_full_light || form.platform_logo_full_dark || form.platform_logo_full;
  const darkLogoUrl = darkLogoId ? `${API}/files/${darkLogoId}` : null;
  const lightLogoUrl = lightLogoId ? `${API}/files/${lightLogoId}` : null;
  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="platform-logo-box">
        {logoUrl ? <img src={logoUrl} alt="شعار المنصة" className="h-16 w-16 rounded-xl border border-slate-100 bg-white object-contain" />
          : <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-slate-100 text-slate-400"><Icon name="grid" className="w-7 h-7" /></div>}
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-800">الرمز (Icon)</div>
          <div className="text-xs text-slate-500">يُستخدم لأيقونة المتصفح (favicon) وتطبيق PWA فقط</div>
        </div>
        <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-teal-300">
          {uploading ? 'جارٍ الرفع...' : 'تغيير الرمز'}
          <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="platform-logo-input" />
        </label>
      </div>
      <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-[#0A192F] p-5" data-testid="platform-logo-full-dark-box">
        {darkLogoUrl ? <img src={darkLogoUrl} alt="الشعار الليلي" className="h-14 max-w-[220px] rounded-lg bg-white/5 object-contain p-1.5" />
          : <div className="flex h-14 w-40 items-center justify-center rounded-xl bg-white/5 text-slate-400 text-xs">لا يوجد شعار ليلي</div>}
        <div className="flex-1">
          <div className="text-sm font-bold text-white">الشعار الليلي (Dark)</div>
          <div className="text-xs text-slate-400">يظهر في الموقع العام عند تفعيل الوضع الليلي (الافتراضي)</div>
        </div>
        <label className="cursor-pointer rounded-lg border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:border-teal-300">
          {uploadingDark ? 'جارٍ الرفع...' : 'تغيير الشعار الليلي'}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadThemeLogo(e, 'dark')} data-testid="platform-logo-full-dark-input" />
        </label>
      </div>
      <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="platform-logo-full-light-box">
        {lightLogoUrl ? <img src={lightLogoUrl} alt="الشعار النهاري" className="h-14 max-w-[220px] rounded-lg border border-slate-100 bg-white object-contain p-1.5" />
          : <div className="flex h-14 w-40 items-center justify-center rounded-xl bg-slate-100 text-slate-400 text-xs">لا يوجد شعار نهاري</div>}
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-800">الشعار النهاري (Light)</div>
          <div className="text-xs text-slate-500">يظهر في الموقع العام عند تفعيل الوضع النهاري</div>
        </div>
        <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-teal-300">
          {uploadingLight ? 'جارٍ الرفع...' : 'تغيير الشعار النهاري'}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadThemeLogo(e, 'light')} data-testid="platform-logo-full-light-input" />
        </label>
      </div>
      <p className="text-xs text-slate-500">إن رفعت شعاراً واحداً فقط، يُستخدم تلقائياً في الوضعين الليلي والنهاري لحين رفع الآخر.</p>
      <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="saudi-badge-box">
        {form.saudi_badge_logo_file_id ? <img src={`${API}/files/${form.saudi_badge_logo_file_id}`} alt="شعار رؤية 2030" className="h-14 w-auto max-w-[140px] rounded-lg border border-slate-100 bg-white object-contain p-1.5" />
          : <div className="flex h-14 w-28 items-center justify-center rounded-xl bg-slate-100 text-slate-400 text-xs">لا يوجد شعار</div>}
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-800">شارة "صُنع في السعودية" + رؤية 2030</div>
          <div className="text-xs text-slate-500">تظهر في سطر مستقل أسفل الفوتر في الصفحة الرئيسية عند التفعيل</div>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input type="checkbox" checked={Boolean(form.saudi_badge_enabled)} onChange={(e) => toggleSaudiBadge(e.target.checked)} data-testid="saudi-badge-toggle" className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
          تفعيل الظهور
        </label>
        <label className="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-teal-300">
          {uploadingBadge ? 'جارٍ الرفع...' : 'تغيير الشعار'}
          <input type="file" accept="image/*" className="hidden" onChange={uploadSaudiBadgeLogo} data-testid="saudi-badge-logo-input" />
        </label>
      </div>
      <form onSubmit={save} className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2" data-testid="superadmin-platform-form">
        {err && <div className="col-span-full rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        <Field label="اسم المنصة"><input className={inputCls} value={form.platform_name || ''} onChange={(e) => setForm({ ...form, platform_name: e.target.value })} data-testid="platform-input-name" /></Field>
        <Field label="بريد التواصل"><input dir="ltr" type="email" className={inputCls} value={form.contact_email || ''} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
        <Field label="هاتف التواصل"><input dir="ltr" className={inputCls} value={form.contact_phone || ''} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
        <div className="flex gap-4">
          <Field label="اللون الأساسي"><input type="color" value={form.primary_color || '#3b82f6'} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-9 w-14 cursor-pointer rounded border border-slate-200" /></Field>
          <Field label="اللون الثانوي"><input type="color" value={form.secondary_color || '#10b981'} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="h-9 w-14 cursor-pointer rounded border border-slate-200" /></Field>
        </div>
        <Field label="تويتر / X"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.twitter_url || ''} onChange={(e) => setForm({ ...form, twitter_url: e.target.value })} data-testid="platform-input-twitter" /></Field>
        <Field label="إنستغرام"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.instagram_url || ''} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} data-testid="platform-input-instagram" /></Field>
        <Field label="سناب شات"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.snapchat_url || ''} onChange={(e) => setForm({ ...form, snapchat_url: e.target.value })} data-testid="platform-input-snapchat" /></Field>
        <Field label="تيك توك"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.tiktok_url || ''} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} data-testid="platform-input-tiktok" /></Field>
        <Field label="يوتيوب"><input dir="ltr" placeholder="https://..." className={inputCls} value={form.youtube_url || ''} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} data-testid="platform-input-youtube" /></Field>
        <div className="col-span-full flex justify-end"><PrimaryBtn type="submit" disabled={saving} data-testid="platform-save-btn">{saving ? 'جارٍ الحفظ...' : 'حفظ الإعدادات'}</PrimaryBtn></div>
      </form>
    </div>
  );
}
